import type { ExampleCosts } from "./constants";
import {
  EXAMPLE_PROFILES,
  EXAMPLE_VEHICLES,
  KM_PER_MILE,
  LITRES_PER_GALLON,
  MINOR_PER_MAJOR,
  MIN_POSITIVE,
  NEW_PROFILE_DEFAULTS,
  NEW_VEHICLE_DEFAULTS,
  PERIOD_MULT,
} from "./constants";
import { defaultNames } from "./content";
import type { CostItem, Data, DistanceUnit, ItemPeriod, Profile, Result, TimePeriod, Vehicle } from "./types";

/**
 * Real GUIDs, so a vehicle or profile keeps the same identity across export and
 * import, which is what lets a re-import recognise it.
 *
 * `crypto.randomUUID` only exists in a secure context, so plain-HTTP origins
 * (a phone testing against a dev server on the LAN, for instance) fall back to
 * the same format built from `getRandomValues`.
 */
export function uid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Amounts, prices, economy figures and distances must be positive; 0 or blank means "not entered yet". */
export function isInvalid(n: number): boolean {
  return !Number.isFinite(n) || n <= 0;
}

export function round(n: number, dp: number): number {
  return parseFloat(n.toFixed(dp));
}

// ---------- Model factories (created in miles; converted if the app is in km) ----------

export function costItem(name: string, amount: number, period: ItemPeriod): CostItem {
  return { id: uid(), name, amount, period };
}

/** A 0-amount item doesn't apply, so it's left out rather than shown as a phantom row. */
function exampleCostItems(costs: ExampleCosts): CostItem[] {
  const { costs: names } = defaultNames;
  return [
    costItem(names.finance, costs.finance, "month"),
    costItem(names.service, costs.service, "year"),
    costItem(names.mot, costs.mot, "year"),
    costs.roadTax > 0 ? costItem(names.roadTax, costs.roadTax, "year") : null,
    costs.roadTaxPerDistance > 0 ? costItem(names.roadTaxPerDistance, costs.roadTaxPerDistance, "distance") : null,
    costItem(names.insurance, costs.insurance, "year"),
  ].filter((i): i is CostItem => i !== null);
}

export function makeProfile(
  name: string,
  distanceAmount: number,
  distancePeriod: TimePeriod,
  homeShare: number,
  id?: string,
): Profile {
  return { id: id ?? uid(), name, distanceAmount, distancePeriod, homeShare };
}

export function newVehicle(unit: DistanceUnit): Vehicle {
  // Sensible starting figures so the calculator works immediately, but they're guesses,
  // so each is flagged "needs completing" until the person edits it.
  const d = NEW_VEHICLE_DEFAULTS;
  const names = defaultNames.costs;
  const costItems = [
    costItem(names.finance, d.finance, "month"),
    costItem(names.service, d.service, "year"),
    costItem(names.mot, d.mot, "year"),
    costItem(names.roadTax, d.roadTax, "year"),
    costItem(names.insurance, d.insurance, "year"),
  ];
  const v: Vehicle = {
    id: uid(),
    name: defaultNames.newVehicle,
    fuel: "ice",
    costItems,
    mpg: d.mpg,
    iceHomePrice: d.iceHomePrice,
    iceAwayPrice: d.iceAwayPrice,
    evMiPerKwh: d.evMiPerKwh,
    evHomeRate: d.evHomeRate,
    evAwayRate: d.evAwayRate,
    review: {
      fields: ["mpg", "iceHomePrice", "iceAwayPrice", "evMiPerKwh", "evHomeRate", "evAwayRate"],
      items: costItems.map((i) => i.id),
    },
  };
  return unit === "km" ? convertVehicle(v, "km") : v;
}

export function newProfile(count: number, unit: DistanceUnit): Profile {
  const d = NEW_PROFILE_DEFAULTS;
  const p = makeProfile(defaultNames.newProfile(count + 1), d.distanceAmount, d.period, d.homeShare);
  return unit === "km" ? convertProfile(p, "km") : p;
}

export function defaultData(): Data {
  const { petrol, electric } = EXAMPLE_VEHICLES;
  return {
    vehicles: [
      {
        id: uid(),
        name: defaultNames.examplePetrol,
        isExample: true,
        fuel: "ice",
        costItems: exampleCostItems(petrol.costs),
        mpg: petrol.mpg,
        iceHomePrice: petrol.iceHomePrice,
        iceAwayPrice: petrol.iceAwayPrice,
        evMiPerKwh: petrol.evMiPerKwh,
        evHomeRate: petrol.evHomeRate,
        evAwayRate: petrol.evAwayRate,
      },
      {
        id: uid(),
        name: defaultNames.exampleElectric,
        isExample: true,
        fuel: "ev",
        costItems: exampleCostItems(electric.costs),
        mpg: electric.mpg,
        iceHomePrice: electric.iceHomePrice,
        iceAwayPrice: electric.iceAwayPrice,
        evMiPerKwh: electric.evMiPerKwh,
        evHomeRate: electric.evHomeRate,
        evAwayRate: electric.evAwayRate,
      },
    ],
    profiles: EXAMPLE_PROFILES.map((p, i) =>
      makeProfile(defaultNames.exampleProfiles[i], p.distanceAmount, "year", p.homeShare),
    ),
    currency: "GBP",
    distanceUnit: "mi",
  };
}

// ---------- Unit conversion ----------
// Stored figures are kept in the current unit at full precision, so switching
// Mi/Km back and forth doesn't drift.

export function swapPerUnitName(name: string, toUnit: DistanceUnit): string {
  if (toUnit === "km") {
    return name.replace(/\b([Pp]er )([Mm])iles?\b/g, (_m, per: string, m: string) => per + (m === "M" ? "Km" : "km"));
  }
  return name.replace(
    /\b([Pp]er )([Kk])(?:m|ilometers?|ilometres?)\b/g,
    (_m, per: string, k: string) => per + (k === "K" ? "Mile" : "mile"),
  );
}

export function convertProfile(p: Profile, toUnit: DistanceUnit): Profile {
  const f = toUnit === "km" ? KM_PER_MILE : 1 / KM_PER_MILE;
  return { ...p, distanceAmount: p.distanceAmount * f };
}

export function convertVehicle(v: Vehicle, toUnit: DistanceUnit): Vehicle {
  const f = toUnit === "km" ? KM_PER_MILE : 1 / KM_PER_MILE;
  return {
    ...v,
    // UK mpg <-> km/L: km/L = mpg × km-per-mile ÷ litres-per-gallon
    mpg: toUnit === "km" ? (v.mpg * KM_PER_MILE) / LITRES_PER_GALLON : (v.mpg * LITRES_PER_GALLON) / KM_PER_MILE,
    evMiPerKwh: v.evMiPerKwh * f,
    costItems: v.costItems.map((item) => ({
      ...item,
      amount: item.period === "distance" ? item.amount / f : item.amount, // p/mi <-> p/km
      name: swapPerUnitName(item.name, toUnit),
    })),
  };
}

export function convertData(d: Data, toUnit: DistanceUnit): Data {
  if (d.distanceUnit === toUnit) return d;
  return {
    ...d,
    distanceUnit: toUnit,
    profiles: d.profiles.map((p) => convertProfile(p, toUnit)),
    vehicles: d.vehicles.map((v) => convertVehicle(v, toUnit)),
  };
}

// ---------- Costs ----------

export function annualDistance(p: Profile): number {
  return p.distanceAmount * PERIOD_MULT[p.distancePeriod];
}

function itemAnnualCost(item: CostItem, p: Profile): number {
  if (item.period === "distance") return (item.amount / MINOR_PER_MAJOR) * annualDistance(p);
  return item.amount * PERIOD_MULT[item.period];
}

export function fixedAnnual(v: Vehicle, p: Profile): number {
  return v.costItems.reduce((sum, item) => sum + itemAnnualCost(item, p), 0);
}

/** Spreading fixed costs needs a non-zero distance; a profile with none is treated as one unit. */
function chargeableDistance(p: Profile): number {
  return Math.max(annualDistance(p), 1);
}

/** Fixed costs as a per-distance figure, in minor units. */
export function fixedPerDistance(v: Vehicle, p: Profile): number {
  return (fixedAnnual(v, p) * MINOR_PER_MAJOR) / chargeableDistance(p);
}

export function compute(v: Vehicle, p: Profile, unit: DistanceUnit): Result {
  const dist = chargeableDistance(p);
  const fixed = fixedAnnual(v, p);
  const fixedPpm = (fixed * MINOR_PER_MAJOR) / dist;
  let homeCost: number;
  let awayCost: number;
  if (v.fuel === "ev") {
    const eff = Math.max(v.evMiPerKwh, MIN_POSITIVE);
    homeCost = v.evHomeRate / eff;
    awayCost = v.evAwayRate / eff;
  } else {
    const economy = Math.max(v.mpg, MIN_POSITIVE);
    const litresFactor = unit === "mi" ? LITRES_PER_GALLON : 1;
    homeCost = (v.iceHomePrice * litresFactor) / economy;
    awayCost = (v.iceAwayPrice * litresFactor) / economy;
  }
  const homeShare = Math.min(Math.max(p.homeShare, 0), 100) / 100;
  const homeContribution = homeCost * homeShare;
  const awayContribution = awayCost * (1 - homeShare);
  const energyPpm = homeContribution + awayContribution;
  return {
    fixedAnnual: fixed,
    fixedPpm,
    homeCost,
    awayCost,
    homeContribution,
    awayContribution,
    energyPpm,
    totalPpm: fixedPpm + energyPpm,
    annualTotal: fixed + (energyPpm / MINOR_PER_MAJOR) * dist,
    homeSharePct: homeShare * 100,
  };
}
